import { ReactNode } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { useRbacEnabled } from '@/hooks/useRbacEnabled';
import { useWhoami } from '@/hooks/useWhoami';
import { Entitlement } from '@/types';

interface EntitledProps {
  /** Which entitlement is required to see/enable the children */
  entitlement: Entitlement;
  /**
   * What to do when the user lacks the entitlement:
   * - `"hide"` (default): render nothing
   * - `"disable"`: render children grayed out with a tooltip
   */
  behavior?: 'hide' | 'disable';
  /** Tooltip text shown when behavior is "disable". Defaults to "You don't have permission to perform this action." */
  disabledTooltip?: string;
  children: ReactNode;
}

/**
 * Wrapper component that shows/hides children based on the current user's entitlements.
 *
 * When the Statsig `enable_rbac` gate is off, this component is transparent
 * and always renders its children (no enforcement).
 *
 * @example
 * ```tsx
 * // Hide a button if the user can't write assets
 * <Entitled entitlement="write_assets">
 *   <Button onClick={handleDelete}>Delete</Button>
 * </Entitled>
 *
 * // Disable (gray out) instead of hiding
 * <Entitled entitlement="manage_accounts" behavior="disable">
 *   <Button onClick={handlePurge}>Purge Account</Button>
 * </Entitled>
 * ```
 */
export function Entitled({
  entitlement,
  behavior = 'hide',
  disabledTooltip = "You don't have permission to perform this action.",
  children,
}: EntitledProps) {
  const rbacEnabled = useRbacEnabled();
  const { hasEntitlement } = useWhoami();

  const allowed = !rbacEnabled || hasEntitlement(entitlement);

  if (allowed) {
    return <>{children}</>;
  }

  if (behavior === 'hide') {
    return null;
  }

  // behavior === 'disable'
  return (
    <Tooltip title={disabledTooltip}>
      <div
        className="pointer-events-none cursor-not-allowed opacity-50"
        aria-disabled="true"
        data-testid="entitled-disabled"
      >
        {children}
      </div>
    </Tooltip>
  );
}
